import React from 'react';
import { compose } from 'redux';
import classnames from 'classnames';
import { withTranslation, WithTranslation } from 'react-i18next';
import ShopThumb, {
  Props as ShopThumbProps,
  ShopThumbType,
} from '@wix/da-shared-react/pkg/DeviationViews/Thumb/Shop';
import IconButtonWithContextMenu from '@wix/da-shared-react/pkg/Dropdown2/Preset/IconButtonWithContextMenu';
import EllipsisIcon from '@wix/da-shared-react/pkg/Icons/Ellipsis';
import LockIcon from '@wix/da-shared-react/pkg/Icons/Lock';
import UnlockIcon from '@wix/da-shared-react/pkg/Icons/Unlock';
import PencilIcon from '@wix/da-shared-react/pkg/Icons/Pencil';
import TrashcanIcon from '@wix/da-shared-react/pkg/Icons/Trashcan';
import { IconSize } from '@wix/da-shared-react/pkg/Icons/IconWrap';
import { BiEventType, BiSpan } from '@wix/da-shared-react/pkg/biLogger';
import {
  withLogBiEvent,
  WithLogBiEventProps,
} from '@wix/da-shared-react/pkg/biLogger/hocs/withLogBiEvent';
import {
  default as CommissionConfirmation,
  Props as CommissionConfirmationProps,
} from '@wix/da-shared-react/pkg/CommissionEditPopup/CommissionConfirmation';
import {
  EditDeviationPayload,
  DeleteDeviationPayload,
} from '@wix/da-shared-react/pkg/redux/deviations/actionCreators';
import { getDeviationResourceType } from '@wix/da-shared-react/pkg/DeviationViews/_helpers';
import { PapiDeviation } from '@wix/da-types-papi';
import {
  CommissionStatusCodeMap,
  CommissionStatusType,
} from '@wix/da-shared-react/dist/src/types/commissions';

import s from './CommissionThumbWithMenu.scss';

export interface ActionProps {
  className?: string;
  isOwner: boolean;
  isActive?: boolean;
  isDeleted?: boolean;
  editDeviation: (object: EditDeviationPayload) => void;
  toggleCommissionStatus: (object: PapiDeviation) => void;
  deleteDeviation: (object: DeleteDeviationPayload) => void;
}

export type Props = ShopThumbProps & ActionProps;

export interface State {
  confirmationPopupType?: CommissionConfirmationProps['confirmationType'];
}

export class CommissionThumbWithMenu extends React.Component<
  Props & WithTranslation & WithLogBiEventProps,
  State
> {
  readonly state: State = {
    confirmationPopupType: undefined,
  };

  openCommissionPauseConfirmation = event => {
    event.preventDefault();
    const { isActive, isDeleted } = this.props;
    if (isDeleted) {
      return;
    }

    this.setState({
      confirmationPopupType: isActive ? 'disable' : 'enable',
    });
  };

  onChangeCommissionStatus = () => {
    const { deviation } = this.props;

    if (!deviation.commission) {
      return;
    }
    this.props.toggleCommissionStatus(deviation);
    this.onCloseConfirmationModal();
  };

  openCommissionDeleteConfirmation = event => {
    event.preventDefault();

    this.setState({
      confirmationPopupType: 'remove',
    });
  };

  onDeleteCommission = () => {
    this.props.deleteDeviation({ deviation: this.props.deviation });
    this.setState({
      confirmationPopupType: undefined,
    });
  };

  onCloseConfirmationModal = () => {
    this.setState({
      confirmationPopupType: undefined,
    });
  };

  openCommissionEditModal = event => {
    const { editDeviation, deviation } = this.props;
    if (!deviation.isTextEditable) {
      return;
    }
    event.preventDefault();

    editDeviation({ deviation });
  };

  onDropdownOpen = () => {
    const { logBiEvent, deviation } = this.props;
    logBiEvent({
      evid: BiEventType.MORE_MENU_CLICK,
      typeid: getDeviationResourceType(deviation),
      itemid: deviation.deviationId,
    });
  };

  render() {
    const { t, isOwner, deviation, className, isActive } = this.props;
    const { confirmationPopupType } = this.state;

    const commissionStatus =
      deviation.commission && deviation.commission.status;
    const isCommissionDisabled =
      commissionStatus ===
      CommissionStatusCodeMap[CommissionStatusType.DISABLED];

    return (
      <div className={classnames(s['root'], className)}>
        <ShopThumb
          {...this.props}
          showAuthor={!isOwner}
          type={ShopThumbType.COMMISSION}
          disabled={isCommissionDisabled}
          disabledLabel="deviationview.commission.commissionClosed"
        />
        {isOwner && (
          <div className={s['dropdown-wrapper']}>
            <IconButtonWithContextMenu
              className={s['dropdown']}
              onOpen={this.onDropdownOpen}
              items={[
                {
                  // safe zone
                  items: [
                    {
                      icon: <PencilIcon />,
                      label: t('common.edit'),
                      onClick: this.openCommissionEditModal,
                      componentClass: BiSpan,
                      componentProps: !deviation.isTextEditable
                        ? {
                            title: t('common.commissions.legacyNotEditable'),
                            style: {
                              pointerEvents: 'all',
                              cursor: 'default',
                              background: 'none',
                            },
                          }
                        : {},
                      disabled: !deviation.isTextEditable,
                    },
                    {
                      icon: isActive ? <UnlockIcon /> : <LockIcon />,
                      label: isActive ? t('common.close') : t('common.open'),
                      onClick: this.openCommissionPauseConfirmation,
                    },
                  ],
                },
                {
                  // danger zone
                  items: [
                    {
                      icon: <TrashcanIcon />,
                      label: t(`common.delete`),
                      onClick: this.openCommissionDeleteConfirmation,
                      danger: true,
                    },
                  ],
                },
              ]}
              withButton
            >
              <EllipsisIcon
                size={IconSize.COMPACT}
                className={s['icon-background']}
              />
            </IconButtonWithContextMenu>
          </div>
        )}
        {confirmationPopupType && (
          <CommissionConfirmation
            isOpen
            confirmationType={confirmationPopupType}
            onCancel={this.onCloseConfirmationModal}
            onConfirm={
              confirmationPopupType === 'remove'
                ? this.onDeleteCommission
                : this.onChangeCommissionStatus
            }
          />
        )}
      </div>
    );
  }
}

const enhance = compose(withTranslation(), withLogBiEvent);

export default enhance(CommissionThumbWithMenu);
